import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './DataQualityPopover.scss';
import { DataQualityState, getBadgeCopy, getPopoverCopy } from './dataQualityHelpers';

interface DataQualityPopoverProps {
  state: DataQualityState;
  /** Visual variant for the trigger badge — pill (default) or chip. */
  size?: 'pill' | 'chip';
}

interface AnchorRect {
  top: number;
  left: number;
  bottom: number;
  width: number;
}

/**
 * Source-aware badge that opens a small popover on click. The popover is
 * rendered through a React portal into `document.body` because the natural
 * mount point (`Title.title-box__row`) has `overflow: hidden`, which clips
 * any `position: absolute` child — a popover rendered there would simply be
 * invisible. Position is computed from the badge's bounding rect, so a
 * portal-rendered popover stays anchored to it regardless of scroll.
 *
 * Renders `null` when no badge applies (no integration, log upload, capability
 * not yet probed).
 */
export const DataQualityPopover: React.FC<DataQualityPopoverProps> = ({
  state,
  size = 'pill',
}) => {
  const { t } = useTranslation();
  const badge = getBadgeCopy(state);
  const popover = getPopoverCopy(state);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  // Recompute the anchor rect whenever the popover opens. Using
  // `useLayoutEffect` (not `useEffect`) avoids a one-frame flicker where the
  // panel would otherwise render at the top-left of the viewport before being
  // moved into place.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setAnchor({
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      width: rect.width,
    });
  }, [open]);

  if (!badge || !popover) return null;

  const triggerClassName = [
    'data-quality-badge',
    `data-quality-badge--${size}`,
    `data-quality-badge--${badge.modifier}`,
    'data-quality-badge--clickable',
  ].join(' ');

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="data-quality-badge__dot" aria-hidden="true" />
        <span className="data-quality-badge__label">{t(badge.labelKey)}</span>
        <span className="ico--info ico--xs data-quality-badge__icon" aria-hidden="true" />
      </button>

      {open &&
        anchor &&
        createPortal(
          <>
            <div
              className="data-quality-popover__backdrop"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div
              className={`data-quality-popover data-quality-popover--${badge.modifier}`}
              role="dialog"
              aria-label={t(popover.titleKey)}
              style={{
                position: 'fixed',
                top: anchor.bottom + 8,
                left: anchor.left,
              }}
            >
              <button
                type="button"
                className="data-quality-popover__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
              <h4 className="data-quality-popover__title">{t(popover.titleKey)}</h4>
              <p className="data-quality-popover__text">{t(popover.bodyKey)}</p>

              {popover.ctaKey && popover.ctaHref && (
                <a
                  className="data-quality-popover__cta"
                  href={popover.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(popover.ctaKey)}
                  <span className="ico--external-link ico--xxs" aria-hidden="true" />
                </a>
              )}

              {popover.secondaryKey && (
                <p className="data-quality-popover__secondary">{t(popover.secondaryKey)}</p>
              )}
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default DataQualityPopover;
